import { createSlice } from "@reduxjs/toolkit";

/**
 * Rebuild service lookup maps from the live catalog (iframe attrs + query defaults).
 * Called after init and whenever a service item is upserted.
 */
function rebuildServiceMaps(state) {
  const jsx = {};
  const query = {};
  if (!state.catalog || typeof state.catalog !== "object") {
    state.serviceEmbedJSXObj = jsx;
    state.serviceEmbedQueryObj = query;
    return;
  }
  for (const item of Object.values(state.catalog)) {
    if (!item || item.type !== "service" || !item.slug) continue;
    const slug = item.slug;
    if (Object.hasOwn(item, "iframeCustomAttributes")) {
      jsx[slug] = item.iframeCustomAttributes;
    }
    if (
      item.defaultEmbedQuery != null &&
      String(item.defaultEmbedQuery).trim() !== ""
    ) {
      query[slug] = String(item.defaultEmbedQuery).trim();
    }
  }
  state.serviceEmbedJSXObj = jsx;
  state.serviceEmbedQueryObj = query;
}

function InitState() {
  const initialState = {};
  initialState.catalog = null;
  initialState.catalogMetadata = null;
  initialState.catalogModel = null;
  initialState.serviceEmbedJSXObj = null;
  initialState.serviceEmbedQueryObj = null;
  return initialState;
}

export const catalogDataSlice = createSlice({
  name: "catalogData",
  initialState: InitState(),
  reducers: {
    initState: (state, action) => {
      const payload = action.payload;

      state.catalog = payload.catalog;
      state.catalogMetadata = payload.catalogMetadata;
      state.catalogModel = payload.catalogModel;
      // Prefer maps from gather; fall back to rebuild from catalog items
      if (
        payload.serviceEmbedJSXObj != null ||
        payload.serviceEmbedQueryObj != null
      ) {
        state.serviceEmbedJSXObj = payload.serviceEmbedJSXObj || {};
        state.serviceEmbedQueryObj = payload.serviceEmbedQueryObj || {};
      } else {
        rebuildServiceMaps(state);
      }
    },

    /** Merge one catalog item after create/update so admin forms see saved fields immediately. */
    upsertItem: (state, action) => {
      const doc = action.payload;
      if (!doc || typeof doc !== "object" || !state.catalog) return;
      const key =
        (doc._id != null && String(doc._id)) ||
        (doc.identifier != null && String(doc.identifier)) ||
        "";
      if (!key) return;

      // Prefer existing store key when we can match by id/identifier
      let storeKey = key;
      if (!state.catalog[storeKey]) {
        const match = Object.entries(state.catalog).find(([, item]) => {
          if (!item) return false;
          return (
            String(item._id) === key ||
            String(item.identifier) === key ||
            String(item.identifier) === String(doc.identifier) ||
            String(item._id) === String(doc._id)
          );
        });
        if (match) storeKey = match[0];
      }

      state.catalog[storeKey] = {
        ...(state.catalog[storeKey] || {}),
        ...doc,
      };

      // Keep service embed maps in sync without a full re-fetch
      if (doc.type === "service" || state.catalog[storeKey]?.type === "service") {
        rebuildServiceMaps(state);
      }
    },
  },
});

// Action creators are generated for each case reducer function
export const catalogDataActions = catalogDataSlice.actions;

export default catalogDataSlice.reducer;
